import React, { useEffect, useState } from 'react';
import { Alert, Accordion, Card, Badge, Row, Col, Form, Button, Table, Modal } from 'react-bootstrap';

function Configs(props) {
    const { socket, pageConfigs, serial, setLoading, user } = props;

    const [formData, setFormData] = useState({});
    const [data, setData] = useState({});
    const [flagIp, setFlagIp] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [showNetworkModal, setShowNetworkModal] = useState(false);

    const [groupedData, setGroupedData] = useState({});
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        setFormData(pageConfigs);
        setIsModified(false);
    }, [pageConfigs])

    useEffect(() => {
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(pageConfigs);
        setIsModified(hasChanges);
    }, [formData, pageConfigs]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isModified) {
                event.preventDefault();
                event.returnValue = "Você tem alterações não salvas. Deseja sair?";
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isModified]);

    useEffect(() => {
        const filterByCountry = (country) => {
            return formData?.charts?.filter((item) => {
                return item?.pais?.includes(country);
            });
        };

        const groupByCategory = (data) => {
            const grouped = data.reduce((acc, item) => {
                const { grupo } = item;
                if (!acc[grupo]) {
                    acc[grupo] = [];
                }
                acc[grupo].push(item);
                return acc;
            }, {});

            // Ordena os grupos alfabeticamente e retorna um novo objeto ordenado
            return Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)));
        };

        if (formData?.selected_chart?.id) {
            setSelectedItem(`${formData?.selected_chart?.id}`);
        }

        if (formData?.pais) {
            const filteredData = filterByCountry(formData.pais);
            setGroupedData(groupByCategory(filteredData));
        }
    }, [formData?.pais]);

    const sanitizeAndParseArray = (nomeString) => {
        try {
            if (!nomeString || typeof nomeString !== "string") return []; // Retorna um array vazio se for null, undefined ou não for string

            let sanitizedString = nomeString
                .trim() // Remove espaços extras no início e no fim
                .replace(/'/g, '"') // Troca aspas simples por duplas para JSON válido
                .replace(/%\s*/g, "") // Remove "%"
                .replace(/\(.*?\)/g, "") // Remove qualquer conteúdo dentro de parênteses (ex: "(EXIT)")
                .replace(/"\s*,\s*]/g, '"]') // Corrige vírgula antes do fechamento do array
                .replace(/"\s*,\s*}/g, '"}') // Corrige vírgula antes do fechamento do objeto JSON
                .replace(/,\s*$/, "") // Remove vírgula final extra
                .replace(/"\s*,\s*"/g, '","') // Garante que os elementos do array estejam corretamente formatados

            if (!sanitizedString.startsWith("[") || !sanitizedString.endsWith("]")) {
                sanitizedString = `[${sanitizedString}]`; // Adiciona colchetes se necessário
            }

            return JSON.parse(sanitizedString);
        } catch (error) {
            return [];
        }
    };

    const getNameByLanguage = (nomeString, idioma) => {
        const nomesArray = sanitizeAndParseArray(nomeString);
        // Mapeia os idiomas para os índices do array
        const languageMap = {
            PT_BR: 0,
            EN_US: 1,
            ES: 2,
            FR: 3,
            IT: 4,
            DE: 5
        };
        // Se idioma não existir no mapa, pega o primeiro (PT_BR)
        const index = languageMap[idioma] ?? 0;
        return nomesArray[index] || nomesArray[0] || "Nome inválido";
    };

    const handleInput = (event) => {
        const { name, value } = event.target;
        const formattedValue = formatIP(value);
        setData(prevData => ({ ...prevData, [name]: formattedValue }));
        setFormData(prevFormData => {
            const updatedNetwork = prevFormData.network.map(networkItem => {
                if (networkItem.device === data.device) {
                    return { ...networkItem, [name]: formattedValue };
                }
                return networkItem;
            });
            return { ...prevFormData, network: updatedNetwork };
        });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        if (name === "porta_local" && (value === "1302" || value === "1303")) {
            alert("As portas 1302 e 1303 são reservadas. Por favor, escolha outra.");
            return;
        }

        if (name === "ip") {
            const formattedValue = formatIP(value);
            setFormData({ ...formData, [name]: formattedValue });
        } else if (name === "porta_remota") {
            setFormData({ ...formData, [name]: value.slice(0, 5) });
        } else if (name === "porta_local") {
            setFormData({ ...formData, [name]: value.slice(0, 5) });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const formatIP = (value) => {
        const cleanedValue = value.replace(/[^0-9.]/g, '');
        const parts = cleanedValue.split('.');
        const formattedParts = parts.map(part => part.slice(0, 3));
        return formattedParts.join('.').slice(0, 15);
    };

    const setSubmit = () => {
        setLoading(true);
        setIsModified(false);

        let data = { ...formData };
        if (data?.charts?.length) {
            delete data.charts;
        }

        if (!serial) {
            setLoading(false);
            console.log("Sem serial!");
            return
        }
        socket.send(
            JSON.stringify({
                type: 'message',
                room: serial,
                email: user?.email || "SGU",
                content: {
                    command: 'web_browser',
                    parameters: {
                        value: 'set_configs',
                        page: 'configs',
                        data: data
                    }
                },
            })
        );
    }

    const handleAllChange = (event) => {
        const checked = !event.target.checked;
        setFormData(prevFormData => ({
            ...prevFormData,
            block: checked,
            block_lang: checked,
            block_temp: checked,
            block_percent: checked,
            block_output: checked,
            block_average: checked,
            block_network: checked,
            block_chart: checked,
            block_clock: checked
        }));
    };

    const handleGetConfigs = () => {
        if (!serial) {
            console.log("Sem serial!");
            return
        }
        setLoading(true);
        socket.send(
            JSON.stringify({
                type: 'message',
                room: serial,
                content: {
                    command: 'web_browser',
                    parameters: {
                        value: 'get_configs',
                        page: 'configs'
                    }
                },
            })
        );
    };

    useEffect(() => {
        handleGetConfigs();
    }, [serial])

    const handleSelectItem = (item, index) => {
        setFormData({ ...formData, selected_chart: item });
        setSelectedItem(`${item.id}`);
    };

    return (
        <>
            <Modal show={showNetworkModal} onHide={() => setShowNetworkModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Configurações de rede</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Essas configurações estão disponíveis apenas quando acessadas diretamente pela mesma rede, utilizando o IP e a porta 3000 do equipamento.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowNetworkModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            <Alert
                variant="warning"
                dismissible
                className="text-center mb-3"
                style={{ fontSize: "0.9rem" }}
            >
                ⚠️ Mantenha seu equipamento sempre atualizado para garantir o funcionamento correto de todas as funções.
            </Alert>

            <Card className='p-2 mb-2' style={{ background: '#f4f4f4' }}>


                <Form style={{ textAlign: 'left' }}>
                    <h4>Envio de resultados ao sistema</h4>
                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Group className="d-flex align-items-center mb-2">
                                <Form.Label style={{ minWidth: 100 }} className="me-2 mb-0 fw-bold">IP:</Form.Label>
                                <Form.Control
                                    className="border-secondary"
                                    type="text"
                                    name="ip"
                                    placeholder="IP"
                                    value={formData?.ip || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>

                            <Form.Group className="d-flex align-items-center mb-2">
                                <Form.Label style={{ minWidth: 100 }} className="me-2 mb-0 fw-bold">Porta:</Form.Label>

                                <Form.Control
                                    className="border-secondary"
                                    type="text"
                                    name="porta_remota"
                                    placeholder="Porta"
                                    value={formData?.porta_remota || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>

                    </Row>

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Label className="form-check-inline mb-2 mt-2"><b>Protocolo:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                type="radio"
                                label="SOCKET"
                                checked={formData?.type === "SOCKET" ? true : false}
                                onChange={event => {
                                    setFormData({ ...formData, type: event.target.checked ? "SOCKET" : "POST" });
                                }}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios1"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                type="radio"
                                label="POST"
                                checked={formData?.type === "POST" ? true : false}
                                onChange={event => {
                                    setFormData({ ...formData, type: event.target.checked ? "POST" : "SOCKET" });
                                }}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios2"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Manter Conexão"
                                checked={formData?.manter_conectado}
                                onChange={event => {
                                    setFormData({ ...formData, manter_conectado: event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios3"
                            />
                        </Col>
                    </Row>

                    <hr />

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <h4>Configurações do equipamento como servidor</h4>
                            <Form.Group className="d-flex align-items-center mb-2">
                                <Form.Label style={{ minWidth: 100 }} className="me-2 mb-0 fw-bold">Porta Local:</Form.Label>
                                <Form.Control
                                    className="border-secondary"
                                    type="text"
                                    name="porta_local"
                                    placeholder="Porta Local"
                                    value={formData?.porta_local || ''}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <h4>Nome do host</h4>
                            <Form.Group className="d-flex align-items-center mb-2">
                                <Form.Label style={{ minWidth: 100 }} className="me-2 mb-0 fw-bold">Hostname:</Form.Label>
                                <Form.Control
                                    className="border-secondary"
                                    type="text"
                                    name="Hostname"
                                    placeholder="Hostname"
                                    value={formData?.hostname || ""}
                                    onChange={event => {
                                        setFormData({ ...formData, hostname: event.target.value });
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <hr />

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Codificação:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                type="radio"
                                label="ASCII"
                                checked={formData?.encode === "ASCII" ? true : false}
                                onChange={event => {
                                    setFormData({ ...formData, encode: event.target.checked ? "ASCII" : "UTF-8" });
                                }}
                                name="formHorizontalRadios2"
                                id="formHorizontalRadios40"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                type="radio"
                                label="UTF-8"
                                checked={formData?.encode === "UTF-8" ? true : false}
                                onChange={event => {
                                    setFormData({ ...formData, encode: event.target.checked ? "UTF-8" : "ASCII" });
                                }}
                                name="formHorizontalRadios2"
                                id="formHorizontalRadios50"
                            />
                        </Col>
                    </Row>
                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={3} xl={3}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Buffer:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label={formData?.output_buffer ? "Ativo" : "Inativo"}
                                checked={formData?.output_buffer}
                                onChange={event => {
                                    setFormData({ ...formData, output_buffer: event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios10"
                            />
                        </Col>
                        <Col xs={12} sm={12} md={6} lg={2} xl={2}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Tamanho do buffer:</b></Form.Label>
                        </Col>
                        <Col xs={12} sm={12} md={6} lg={1} xl={1}>
                            <Form.Select
                                size='sm'
                                className="mb-0 border-secondary"
                                aria-label="Tamanho do Buffer"
                                value={formData?.size_buffer}
                                onChange={event => {
                                    setFormData({ ...formData, size_buffer: Number(event.target.value) });
                                }}
                            >
                                <option value="1">1</option>
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="20">20</option>
                                <option value="40">40</option>
                                <option value="60">60</option>
                                <option value="80">80</option>
                                <option value="100">100</option>
                            </Form.Select>
                        </Col>
                    </Row>
                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Wi-fi:</b></Form.Label>
                            <Form.Check
                                isValid
                                title='Status do Wi-fi'
                                className="form-check-inline mb-2"
                                label={formData?.wifi ? "Ativo" : "Inativo"}
                                checked={formData?.wifi}
                                onChange={event => {
                                    setFormData({ ...formData, wifi: event.target.checked ? true : false });
                                }}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios11"
                                disabled={true}
                            />
                        </Col>
                    </Row>

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Média:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label={formData?.media ? "Ativo" : "Inativo"}
                                checked={formData?.media}
                                onChange={event => {
                                    setFormData({ ...formData, media: event.target.checked ? true : false });
                                }}
                                name="formHorizontalRadios"
                                id="media"
                            />
                        </Col>
                    </Row>

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Idioma:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Português"
                                checked={formData?.idioma === "PT_BR"}
                                onChange={event => {
                                    setFormData({ ...formData, idioma: event.target.checked ? "PT_BR" : "" });
                                }}
                                name="formHorizontalRadios15"
                                id="PT_BR"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Inglês"
                                checked={formData?.idioma === "EN_US"}
                                onChange={event => {
                                    setFormData({ ...formData, idioma: event.target.checked ? "EN_US" : "" });
                                }}
                                name="formHorizontalRadios15"
                                id="EN_US"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Espanhol"
                                checked={formData?.idioma === "ES"}
                                onChange={event => {
                                    setFormData({ ...formData, idioma: event.target.checked ? "ES" : "" });
                                }}
                                name="formHorizontalRadios15"
                                id="ES"
                                type="radio"
                            />
                        </Col>
                    </Row>

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Saída de dados:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Json"
                                checked={formData?.saida === "json"}
                                onChange={event => {
                                    setFormData({ ...formData, saida: event.target.checked ? "json" : "" });
                                }}
                                name="formHorizontalRadios16"
                                id="Json"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Json_b64"
                                checked={formData?.saida === "json_b64"}
                                onChange={event => {
                                    setFormData({ ...formData, saida: event.target.checked ? "json_b64" : "" });
                                }}
                                name="formHorizontalRadios16"
                                id="json_b64"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Txt"
                                checked={formData?.saida === "txt"}
                                onChange={event => {
                                    setFormData({ ...formData, saida: event.target.checked ? "txt" : "" });
                                }}
                                name="formHorizontalRadios16"
                                id="txt"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="Txt_b64"
                                checked={formData?.saida === "txt_b64"}
                                onChange={event => {
                                    setFormData({ ...formData, saida: event.target.checked ? "txt_b64" : "" });
                                }}
                                name="formHorizontalRadios16"
                                id="txt_b64"
                                type="radio"
                            />
                        </Col>
                    </Row>

                    <hr />

                    <Row className="g-2">
                        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <h4>Troca de curva</h4>
                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Padrão de curva:</b></Form.Label>
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="BRA"
                                checked={formData?.pais === "BRA"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "BRA" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="BRA"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="EUA"
                                checked={formData?.pais === "EUA"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "EUA" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="EUA"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="ESP"
                                checked={formData?.pais === "ESP"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "ESP" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="ESP"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="ALE"
                                checked={formData?.pais === "ALE"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "ALE" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="ALE"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="CGC"
                                checked={formData?.pais === "CGC"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "CGC" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="CGC"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="MEX"
                                checked={formData?.pais === "MEX"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "MEX" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="MEX"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="ARG"
                                checked={formData?.pais === "ARG"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "ARG" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="ARG"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="BOL"
                                checked={formData?.pais === "BOL"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "BOL" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="BOL"
                                type="radio"
                            />
                            <Form.Check
                                isValid
                                className="form-check-inline mb-2"
                                label="ITA"
                                checked={formData?.pais === "ITA"}
                                onChange={event => {
                                    setFormData({ ...formData, pais: event.target.checked ? "ITA" : "" });
                                }}
                                name="formHorizontalRadiosCharts"
                                id="ITA"
                                type="radio"
                            />

                            <br />

                            <Form.Label className="form-check-inline mb-1 mt-1"><b>Curva atual:</b></Form.Label>
                            <Form.Label className="form-check-inline mb-2 mt-1">{getNameByLanguage(formData?.selected_chart?.nome, formData?.selected_chart)}</Form.Label>

                            <Accordion className="mt-1">
                                {Object.keys(groupedData).map((grupo, index) => (
                                    <Accordion.Item eventKey={index.toString()} key={grupo}>
                                        <Accordion.Header>
                                            {grupo}{' '}
                                            <Badge bg="secondary" className="ms-2">
                                                {groupedData[grupo].length}
                                            </Badge>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <Row xs={1} sm={2} md={3} lg={4}>
                                                {groupedData[grupo].map((item, idx) => {
                                                    const itemKey = `${item.id}`;
                                                    const isSelected = selectedItem === itemKey;
                                                    return (
                                                        <Col key={idx} className="mb-3">
                                                            <Card
                                                                className='box-sh'
                                                                bg={isSelected ? 'success' : 'light'}
                                                                text={isSelected ? 'white' : 'dark'}
                                                                onClick={() => handleSelectItem(item, idx)}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <Card.Body className='p-2' style={{ minHeight: 50 }}>
                                                                    <Card.Title className="mb-0 d-flex align-items-center small">
                                                                        {getNameByLanguage(item.nome, formData.idioma)}
                                                                    </Card.Title>
                                                                </Card.Body>
                                                                <Card.Footer className='p-2'>
                                                                    <Button
                                                                        variant={isSelected ? 'light' : 'success'}
                                                                        className="mt-2 w-100"
                                                                    >
                                                                        {isSelected ? 'Selecionado' : 'Selecionar'}
                                                                    </Button>
                                                                </Card.Footer>
                                                            </Card>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                ))}
                            </Accordion>
                        </Col>
                    </Row>


                    <hr />
                    <Row className="g-2">

                        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <h4>Autorizações no equipamento</h4>
                        </Col>

                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Mudar curva no equipamento"
                                checked={!formData?.block_selected_chart}
                                onChange={event => {
                                    setFormData({ ...formData, block_selected_chart: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="MudarCurva"
                            />

                            {formData?.disable_media_acima !== undefined ? (
                                <Form.Check
                                    isValid
                                    className="mb-2"
                                    label="Suspender média em condições de alta umidade"
                                    checked={formData?.disable_media_acima}
                                    onChange={event => {
                                        setFormData({ ...formData, disable_media_acima: event.target.checked });
                                    }}
                                    name="formHorizontalRadios"
                                    id="disable_media_acima"
                                />
                            ) : null}

                        </Col>

                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>

                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Medir apenas lote e placa"
                                checked={formData?.lote_required}
                                onChange={event => {
                                    setFormData({ ...formData, lote_required: event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="lote_placa"
                            />

                            {formData?.require_ticket !== undefined ? (
                                <Form.Check
                                    isValid
                                    className="mb-2"
                                    label="Medir apenas com ticket"
                                    checked={formData?.require_ticket}
                                    onChange={event => {
                                        setFormData({ ...formData, require_ticket: event.target.checked });
                                    }}
                                    name="formHorizontalRadios"
                                    id="require_ticket"
                                />
                            ) : null}

                        </Col>
                    </Row>

                    <hr />
                    <Row className="g-2">

                        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                            <h4>Menu de configurações do equipamento</h4>
                            <Form.Check
                                isValid
                                className="mb-3"
                                label={!formData?.block ? "Todos Ativos" : "Todos Inativos"}
                                checked={!formData?.block}
                                onChange={handleAllChange}
                                name="formHorizontalRadios"
                                id="formHorizontalRadios8"
                            />
                        </Col>


                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Idioma"
                                checked={!formData?.block_lang}
                                onChange={event => {
                                    setFormData({ ...formData, block_lang: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Idioma"
                            />
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Temperatura"
                                checked={!formData?.block_temp}
                                onChange={event => {
                                    setFormData({ ...formData, block_temp: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Temperatura"
                            />
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Percentual"
                                checked={!formData?.block_percent}
                                onChange={event => {
                                    setFormData({ ...formData, block_percent: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Percentual"
                            />
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Saída de dados"
                                checked={!formData?.block_output}
                                onChange={event => {
                                    setFormData({ ...formData, block_output: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Saidadedados"
                            />
                        </Col>

                        <Col xs={12} sm={12} md={6} lg={6} xl={6}>

                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Média"
                                checked={!formData?.block_average}
                                onChange={event => {
                                    setFormData({ ...formData, block_average: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Media"
                            />
                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Rede"
                                checked={!formData?.block_network}
                                onChange={event => {
                                    setFormData({ ...formData, block_network: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Rede"
                            />

                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Relógio"
                                checked={!formData?.block_clock}
                                onChange={event => {
                                    setFormData({ ...formData, block_clock: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Relógio"
                            />

                            <Form.Check
                                isValid
                                className="mb-2"
                                label="Padrão de curva"
                                checked={!formData?.block_chart}
                                onChange={event => {
                                    setFormData({ ...formData, block_chart: !event.target.checked });
                                }}
                                name="formHorizontalRadios"
                                id="Curva"
                            />

                        </Col>
                    </Row>
                    <hr />
                    <Row className="g-2">
                        <Col>
                            <h4>Conexões</h4>
                            <Form.Label>Escolha uma rede:</Form.Label>
                            <Table style={{ fontSize: "0.9rem" }} striped bordered hover="true" responsive size='sm'>
                                <thead>
                                    <tr className="table-secondary" style={{ textAlign: "center" }}>
                                        <th className='col-sm-2'>IP</th>
                                        <th className='col-sm-2'>NAME</th>
                                        <th className='col-sm-2'>GATEWAY</th>
                                        <th className='col-sm-2'>DHCP</th>
                                        <th className='col-sm-2'>MAC</th>
                                        <th className='col-sm-2'>MASK</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {formData?.network?.length > 0 ? formData?.network?.map(item => (
                                        <tr
                                            key={item?.ip}
                                            style={{ textAlign: "center", cursor: "pointer" }}
                                            onClick={() => setShowNetworkModal(true)}
                                        >
                                            <td>{item?.ip || '--'}</td>
                                            <td>{item?.name || '--'}</td>
                                            <td>{item?.gateway || '--'}</td>
                                            <td>{item?.dhcp || '--'}</td>
                                            <td>{item?.mac || '--'}</td>
                                            <td>{item?.mask || '--'}</td>
                                        </tr>
                                    )) : null}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>

                    {flagIp === true ?
                        <Row className="g-2">
                            <Form style={{ textAlign: 'left' }}>
                                <hr />
                                <h4>Rede: {data?.name || "--"}</h4>
                                <Col>
                                    <Form.Check
                                        className="mb-2"
                                        type="radio"
                                        label="Automático (DHCP)"
                                        checked={data?.dhcp === "auto" ? true : false}
                                        onChange={event => {
                                            setData({ ...data, dhcp: event.target.checked ? "auto" : "manual" });
                                        }}
                                        name="formHorizontalRadios3"
                                        id="formHorizontalRadios4"
                                    />
                                    <Form.Check
                                        className="mb-2"
                                        type="radio"
                                        label="Manual"
                                        checked={data?.dhcp === "manual" ? true : false}
                                        onChange={event => {
                                            setData({ ...data, dhcp: event.target.checked ? "manual" : "auto" });
                                        }}
                                        name="formHorizontalRadios3"
                                        id="formHorizontalRadios5"
                                    />
                                </Col>

                                <Row>
                                    <Col xs={12} sm={12} md={6} lg={6} xl={3}>
                                        <Form.Label>IP</Form.Label>
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            name="ip"
                                            placeholder="IP"
                                            value={data?.ip || ''}
                                            onChange={handleInput}
                                            disabled={data?.dhcp === "auto" ? true : false}
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6} lg={6} xl={3}>
                                        <Form.Label>Mask</Form.Label>
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            name="mask"
                                            placeholder="Mask"
                                            value={data?.mask || ''}
                                            onChange={handleInput}
                                            disabled={data?.dhcp === "auto" ? true : false}
                                        />
                                    </Col>

                                    <Col xs={12} sm={12} md={6} lg={6} xl={3}>
                                        <Form.Label>Gateway</Form.Label>
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            name="gateway"
                                            placeholder="Gateway"
                                            value={data?.gateway || ''}
                                            onChange={handleInput}
                                            disabled={data?.dhcp === "auto" ? true : false}
                                        />
                                    </Col>

                                    <Col xs={12} sm={12} md={6} lg={6} xl={3}>
                                        <Form.Label>DNS</Form.Label>
                                        <Form.Control
                                            className="mb-2"
                                            type="text"
                                            name="dns"
                                            placeholder="Dns"
                                            value={data?.dns || ''}
                                            onChange={handleInput}
                                            disabled={data?.dhcp === "auto" ? true : false}
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        </Row>
                        : null
                    }

                    <Button
                        className='col-sm-3 mt-3'
                        variant="success"
                        onClick={() => {
                            setSubmit()
                        }}
                    >
                        Salvar
                    </Button>
                </Form>
            </Card >
        </>
    );
}

export default Configs;
